import { qfRestGet } from './qfClient';

/**
 * `GET /v1/contract-leaders` — the three contract leaderboards in one request.
 *
 * Session volume, open interest, and open-interest change, each ranked
 * server-side. All three Overview panels are served by this single call.
 *
 * ### The important change: ranking moved to the server
 *
 * These panels used to hold one snapshot of the chain and sort it three ways
 * client-side. That is why the old path could mis-rank both OI panels — the
 * snapshot was a *volume*-ranked slice, so anything with heavy open interest
 * and quiet volume never entered the client's view to be re-sorted. Ranking by
 * OI within a set chosen by volume is not a ranking by OI.
 *
 * Three arrays arrive here already correct, each ranked over the whole chain.
 * Nothing downstream should re-sort them.
 *
 * ⚠️ Superseded `/v1/most-active`, `/v1/open-interest` and `/v1/oi-changes`,
 * which mis-ranked, fabricated a previous OI where none existed, and sat behind
 * a static-secret gate respectively.
 */

/** ⚠️ The wire shape. `snake_case`. */
export interface ContractLeader {
  /** `YYYY-MM-DD`. */
  expiry: string;
  strike: number;
  /** `Call | Put`. Capitalised on the wire. */
  option_type: 'Call' | 'Put';

  /** Session volume, from `volume_session_date` — not the OI settle date. */
  volume: number;
  /** Current session's open interest. */
  open_interest: number;

  /**
   * Prior session's open interest.
   *
   * ⚠️ **Null when the contract had no prior observation** — a new listing, or
   * a strike that first traded today.
   */
  prev_open_interest: number | null;

  /**
   * Settle-over-settle change in open interest.
   *
   * ⚠️ **Null means unknown, and must never render as `+0` or "opened from
   * zero".** Both of those are claims: one says the position was unchanged,
   * the other says it was built today from nothing. The honest display for an
   * unknown prior is a dash.
   *
   * ⚠️ **This is not live.** OI publishes around 07:00 ET and reflects the
   * previous settle, so it does not move during the session however hard the
   * contract trades. `volume` is the live figure.
   */
  oi_change: number | null;

  implied_volatility: number | null;
  last_price: number | null;

  /**
   * Volume-weighted aggressor split, as percentages.
   *
   * Sum to ~100 when present — volume with no identifiable side is excluded
   * rather than assigned. All three null means the session produced no split.
   */
  bid_pct: number | null;
  mid_pct: number | null;
  ask_pct: number | null;
}

/** ⚠️ The wire shape. */
export interface ContractLeadersResponse {
  symbol: string;

  /**
   * The OI settle session these OI rankings cover.
   *
   * ⚠️ **Not the volume session.** After 07:00 ET this is today's settle
   * while volume can still be yesterday. Caption Highest OI / Largest OI
   * Changes from this pair; caption Highest Volume from `volume_session_date`.
   *
   * ⚠️ **An empty string means the data is unavailable.** Paired with empty
   * arrays, it is the degraded response — show an empty state rather than
   * spinning.
   */
  session_date: string;
  /** The session `oi_change` is measured against. */
  prev_session_date: string;
  /**
   * Session the volume ranking covers, `YYYY-MM-DD`.
   *
   * Last of today + 2 prior sessions with `volume > 0` for this symbol.
   * Empty string if none of those three days has volume.
   */
  volume_session_date: string;

  /** Ranked by session volume, descending. */
  volume: ContractLeader[];
  /** Ranked by current open interest, descending. */
  open_interest: ContractLeader[];
  /**
   * Ranked by open-interest change.
   *
   * ⚠️ **May be empty while `volume` and `open_interest` are populated.** That
   * is the pre-settlement state: the current session's OI is known but has
   * nothing to diff against yet. It is a "not yet" empty state, not a claim
   * that no position changed — and emphatically not a reason to render zeros.
   */
  oi_change: ContractLeader[];
}

export interface ContractLeadersParams {
  symbol: string;
  /** Rows per ranking, 1–200. Defaults to 50 server-side. */
  limit?: number;
}

/**
 * All three leaderboards for one symbol.
 *
 * ⚠️ **Index weeklies: pass the root the reader chose.** `SPXW` and `NDXP` are
 * expanded into their families server-side. Mapping `SPXW → SPX` here would
 * silently swap the weeklies for the monthlies — a different set of contracts
 * under the same heading. See the `spx-ticker-rename` note for why these two
 * are routinely confused.
 */
export async function fetchContractLeaders(
  params: ContractLeadersParams
): Promise<ContractLeadersResponse> {
  const query: Record<string, string> = {
    symbol: params.symbol.trim().toUpperCase(),
  };
  if (params.limit != null) query.limit = String(params.limit);

  const res = await qfRestGet<ContractLeadersResponse>(
    '/v1/contract-leaders',
    query
  );
  return {
    ...res,
    session_date: res.session_date ?? '',
    prev_session_date: res.prev_session_date ?? '',
    volume_session_date: res.volume_session_date ?? '',
  };
}

/**
 * True when the response is the degraded "no data" shape.
 *
 * Distinguished from a quiet-but-real session, which has a `session_date` and
 * may legitimately have short lists.
 */
export function isLeadersUnavailable(data: ContractLeadersResponse | null | undefined): boolean {
  if (!data) return false;
  return !data.session_date && !data.volume.length && !data.open_interest.length;
}
